import clsx from "clsx";
import { useState } from "react";
import type { DropEvent } from "react-aria";
import { DropZone } from "react-aria-components";
import { useTranslation } from "react-i18next";

import { PlusIcon } from "@/assets/icons/general/Plus";
import { Button } from "@/components/ui/buttons/Button/Button";
import { Typography } from "@/components/ui/text/Typography/Typography";
import useFileValidationCheck from "@/components/uploads/useFileValidationCheck";

interface IProps {
  onFilesDropped: (files: File[]) => void;
  onUploadClick: () => void;
}

const UploadsPageDropZone = ({ onFilesDropped, onUploadClick }: IProps) => {
  const { t } = useTranslation();
  const [isDropZoneOpen, setIsDropZoneOpen] = useState(false);
  const { checkForValidNonSecurePdfFiles } = useFileValidationCheck();

  const handleDrop = async (e: DropEvent) => {
    if (e.items) {
      const files = await Promise.all(e.items.filter((item) => item.kind === "file").map((item) => item.getFile()));

      const validFiles = await checkForValidNonSecurePdfFiles(files, { maxSizeMB: 20 });
      if (validFiles?.length) {
        onFilesDropped(validFiles);
      }
    }
  };

  return (
    <div
      className={clsx(
        "m-10 flex h-full flex-1 items-center justify-center",
        isDropZoneOpen && "rounded-xs border border-[#3B82F6] border-dashed bg-[#3b82f61f]",
      )}
    >
      <DropZone
        className={clsx("flex h-full w-full flex-col items-center justify-center gap-6")}
        onDropEnter={() => setIsDropZoneOpen(true)}
        onDropExit={() => setIsDropZoneOpen(false)}
        onDrop={handleDrop}
      >
        <Typography size="body-3">{t("uploads.userUploads.drag-and-drop")}</Typography>
        <Button
          icon={PlusIcon}
          variant="outlined"
          width="auto"
          iconClassName="w-3 h-3"
          className="h-[32px] px-3 py-2"
          labelClassName="text-label-3"
          onPress={onUploadClick}
        >
          {t("uploads.userUploads.upload-cv")}
        </Button>
      </DropZone>
    </div>
  );
};

export default UploadsPageDropZone;
